<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import type { TScheduleStatus } from '@/domain/catalog/availability';
  import {
    capitalize,
    scheduleBadgeText,
  } from '@/domain/catalog/catalog-status-text';
  import { scheduleSummary } from '@/domain/catalog/schedule-form';
  import { ICategory } from '@/interfaces/ICategory';

  /**
   * A category card's header (plan §5.8, DESIGN §3.5): chevron, name (opens
   * the editor), badges, «6 productos · 1 no disponible · descripción», the
   * switch and ⋯; in selection mode a «Todos» checkbox.
   */
  const props = withDefaults(
    defineProps<{
      category: ICategory;
      /** Every product of the category. */
      count: number;
      /** Products matching the filter or search (null when not filtering). */
      matchCount?: number | null;
      /** Products that cannot be ordered here (hidden). */
      unavailableCount: number;
      /** Can be ordered and sold here (the switch). */
      available: boolean;
      hiddenHere: boolean;
      schedule?: TScheduleStatus | null;
      branchName: string;
      collapsed: boolean;
      /** Collapse is off while a filter or search is active. */
      collapsible: boolean;
      editing?: boolean;
      selecting?: boolean;
      allSelected?: boolean;
      canToggle: boolean;
      online: boolean;
      toggleBlockedTitle?: string | null;
    }>(),
    {
      schedule: null,
      matchCount: null,
      editing: false,
      selecting: false,
      allSelected: false,
      toggleBlockedTitle: null,
    }
  );

  const emit = defineEmits<{
    (e: 'collapse'): void;
    (e: 'open'): void;
    (e: 'toggle', payload: { on: boolean; anchor: HTMLElement | null }): void;
    (e: 'menu', anchor: HTMLElement): void;
    (e: 'select-all'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const switchAnchor = ref<HTMLElement | null>(null);

  const scheduleBadge = computed<{ text: string; tone: string } | null>(() => {
    if (!props.category.hourRanges?.length) return null;
    if (props.schedule?.key === 'scheduleInactive')
      return { text: t('menuPage.status.scheduleInactive'), tone: 'neutral' };
    if (props.schedule?.key === 'outOfHours')
      return { text: t('menuPage.status.outOfHours'), tone: 'amber' };
    const text = scheduleBadgeText(
      t,
      scheduleSummary(props.category.hourRanges)
    );
    return text ? { text: capitalize(text), tone: 'sky' } : null;
  });

  const meta = computed(() => {
    // «5 de 6 productos» while a filter or search shows only some.
    const parts = [
      props.matchCount !== null && props.matchCount !== props.count
        ? tc('menuPage.card.matches', props.count, {
            n: props.matchCount,
            total: props.count,
          })
        : tc('menuPage.card.products', props.count, { n: props.count }),
    ];
    if (props.unavailableCount)
      parts.push(
        tc('menuPage.card.unavailable', props.unavailableCount, {
          n: props.unavailableCount,
        })
      );
    const description = props.category.description?.trim();
    if (description) parts.push(description);
    return parts.join(' · ');
  });

  const writeTitle = computed(() =>
    props.online ? undefined : t('menuPage.errors.offline')
  );

  function onMenu(event: MouseEvent) {
    if (event.currentTarget instanceof HTMLElement)
      emit('menu', event.currentTarget);
  }
</script>

<template>
  <header
    class="v2-catalog-category-header"
    :class="{ 'is-editing': editing, 'no-lead': !selecting && !collapsible }"
  >
    <label v-if="selecting" class="header-check">
      <input
        type="checkbox"
        class="check-input"
        :checked="allSelected"
        :aria-label="t('menuPage.card.selectAll', { name: category.name })"
        @change="emit('select-all')"
      />
    </label>
    <button
      v-else-if="collapsible"
      type="button"
      class="header-chevron"
      :aria-expanded="collapsed ? 'false' : 'true'"
      :aria-label="
        t(collapsed ? 'menuPage.card.expand' : 'menuPage.card.collapse', {
          name: category.name,
        })
      "
      @click="emit('collapse')"
    >
      <i
        class="mdi mdi-chevron-down chevron-icon"
        :class="{ 'is-collapsed': collapsed }"
      />
    </button>

    <div class="header-main">
      <div class="header-title-row">
        <button
          type="button"
          class="header-name"
          :title="category.name"
          @click="emit('open')"
        >
          {{ category.name }}
        </button>
        <span v-if="category.posOnly" class="header-badge tone-sky">
          <i class="mdi mdi-cash-register badge-icon" />{{
            t('menuPage.card.posOnly')
          }}
        </span>
        <span v-if="!category.visible" class="header-badge tone-neutral">{{
          t('menuPage.categoryStatus.hidden')
        }}</span>
        <span v-else-if="hiddenHere" class="header-badge tone-neutral">{{
          t('menuPage.categoryStatus.hiddenHere', { branch: branchName })
        }}</span>
        <span
          v-if="scheduleBadge"
          class="header-badge"
          :class="`tone-${scheduleBadge.tone}`"
        >
          <i class="mdi mdi-clock-outline badge-icon" />{{ scheduleBadge.text }}
        </span>
      </div>
      <p class="header-meta" :title="meta">{{ meta }}</p>
    </div>

    <span
      ref="switchAnchor"
      class="header-switch"
      :title="toggleBlockedTitle || writeTitle"
    >
      <PosUiSwitch
        :value="available"
        :disabled="!canToggle || !online || !!toggleBlockedTitle"
        :aria-label="t('menuPage.card.available', { name: category.name })"
        @input="emit('toggle', { on: $event, anchor: switchAnchor })"
      />
    </span>
    <button
      type="button"
      class="header-menu"
      :aria-label="t('menuPage.rowMenu.title', { name: category.name })"
      @click="onMenu"
    >
      <i class="mdi mdi-dots-horizontal" />
    </button>
  </header>
</template>

<style scoped lang="scss">
  .v2-catalog-category-header {
    @apply tw-flex tw-items-center tw-rounded-t-xl tw-py-2 tw-pl-1 tw-pr-2;
    min-height: 64px;

    // Open in the editor: the list's one orange «you are here».
    &.is-editing {
      @apply tw-bg-orange-50;
      box-shadow: inset 3px 0 0 rgb(var(--c-orange-600));
    }

    .header-check {
      @apply tw-flex tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center;

      .check-input {
        @apply tw-h-5 tw-w-5 tw-cursor-pointer;
        accent-color: rgb(var(--c-neutral-900));
      }
    }

    .header-chevron {
      @apply tw-flex tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-2xl tw-text-neutral-500;

      &:hover {
        @apply tw-bg-neutral-100 tw-text-neutral-800;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .chevron-icon {
        @apply tw-transition-transform tw-duration-150;

        &.is-collapsed {
          @apply -tw-rotate-90;
        }
      }
    }

    .header-main {
      @apply tw-ml-1 tw-min-w-0 tw-flex-1;
    }

    // No chevron (filtering): the name lines up with the rows' photos.
    &.no-lead .header-main {
      @apply tw-ml-2;
    }

    .header-title-row {
      @apply tw-flex tw-flex-wrap tw-items-center;
    }

    .header-name {
      // 24 px of text, 44 px to the touch: the padding is taken back by the
      // margins (it truncates, so an ::after hit area would be clipped).
      @apply -tw-my-2.5 tw-mr-2 tw-max-w-full tw-cursor-pointer tw-truncate tw-rounded tw-border-0 tw-bg-transparent tw-px-0 tw-py-2.5 tw-text-left tw-text-base tw-font-semibold tw-leading-6 tw-text-neutral-900;

      &:hover {
        @apply tw-underline;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .header-badge {
      @apply tw-my-0.5 tw-mr-1.5 tw-inline-flex tw-h-6 tw-max-w-full tw-items-center tw-truncate tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-text-xs tw-font-medium;

      .badge-icon {
        @apply tw-mr-1 tw-text-sm tw-leading-none;
      }

      &.tone-sky {
        @apply tw-bg-sky-50 tw-text-sky-700;
      }

      &.tone-amber {
        @apply tw-bg-amber-50 tw-text-amber-800;
      }

      &.tone-neutral {
        @apply tw-bg-neutral-100 tw-text-neutral-600;
      }
    }

    .header-meta {
      @apply tw-m-0 tw-truncate tw-text-[13px] tw-leading-5 tw-text-neutral-500;
    }

    .header-switch {
      @apply tw-ml-3 tw-flex tw-flex-none;
    }

    .header-menu {
      @apply tw-ml-1 tw-flex tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

      &:hover {
        @apply tw-bg-neutral-100 tw-text-neutral-800;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
